import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SportsGolfRoundedIcon from "@mui/icons-material/SportsGolfRounded";
import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";
import LoadingCircle from "../../utils/LoadingCircle";
import "./ReservationAccount.css";

interface PropsType {
  userState: userState;
}

interface Month {
  value: string;
  label: string;
}

interface ReservationDetail {
  inputDt?: string;
  reservDt?: string;
  reservOption?: string;
  deviceNum?: string;
}

interface AccountRow {
  pk?: string;
  sk?: string;
  facilityLabel?: string;
  communityPrice?: number | string;
  reservState?: string;
  inputDt?: string;
  reservDt?: string;
  reservDate?: string;
  useStartTime?: string;
  useEndTime?: string;
  useTime?: string;
  info_01?: string;
  reservationId?: string;
  slot?: string;
  reservInfo?: {
    pk?: string;
    sk?: string;
    reservations?: Record<string, ReservationDetail>;
  };
}

interface ScreenHistoryEntry {
  row: AccountRow;
  dateKey: string;
  dateLabel: string;
  deviceLabel: string;
  timeLabel: string;
}

const mainApi = new HttpMainApi();
const ROBOT_CLEANER = "ROBOT_CLEANER";
const ROBOT_CLEANER_LABEL = "창문 로봇청소기";
const ACTIVE_RESERVATION_STATE = "신청";

function getFirstReservation(row: AccountRow) {
  const reservations = row.reservInfo?.reservations;
  return reservations ? Object.values(reservations)[0] : undefined;
}

function getFacilityCode(row: AccountRow) {
  return row.pk?.split("/")[1] || "";
}

function getReservationDevice(row: AccountRow) {
  return row.reservInfo?.sk || getFirstReservation(row)?.deviceNum || row.info_01 || "";
}

function getFacilityLabel(row: AccountRow) {
  if (row.facilityLabel) return row.facilityLabel;

  switch (getFacilityCode(row)) {
    case "GOLF":
      return "골프장";
    case "HEALTH":
      return "헬스장";
    case "STUDY":
      return "독서실";
    case "LOCKER":
      return getReservationDevice(row) || "락커";
    case "SCREEN":
      return getReservationDevice(row) === "ROOM" ? "스크린게임" : "스크린타석";
    case "BOOKCAFE":
      return "북카페 예약";
    case "TABLETENNIS":
      return "탁구장 예약";
    default:
      return "커뮤니티 시설";
  }
}

function getReservationDate(row: AccountRow) {
  return row.reservDt || getFirstReservation(row)?.reservDt || row.reservDate || "이용일 정보 없음";
}

function getReservationTime(row: AccountRow) {
  if (row.useTime) return row.useTime;
  if (row.slot) return row.slot;
  if (row.useStartTime && row.useEndTime) return `${row.useStartTime} ~ ${row.useEndTime}`;
  return getFirstReservation(row)?.reservOption || "";
}

function getCommunityPrice(row: AccountRow) {
  const price = Number(row.communityPrice);
  return Number.isFinite(price) && price > 0 ? price : 0;
}

function formatCurrency(value: number) {
  return `${value.toLocaleString("ko-KR")}원`;
}

function formatScreenDate(row: AccountRow, selectedMonth: string) {
  const match = row.reservInfo?.pk?.match(/^(\d{8})\/SCREEN$/);
  if (!match || !match[1].startsWith(selectedMonth)) return null;

  const dateKey = match[1];
  const year = Number(dateKey.slice(0, 4));
  const month = Number(dateKey.slice(4, 6));
  const day = Number(dateKey.slice(6, 8));
  const date = new Date(year, month - 1, day);
  const normalizedKey = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}${String(
    date.getDate(),
  ).padStart(2, "0")}`;
  if (normalizedKey !== dateKey) return null;

  return {
    dateKey,
    dateLabel: getFirstReservation(row)?.reservDt || `${year}년 ${String(month).padStart(2, "0")}월 ${String(day).padStart(2, "0")}일`,
  };
}

function normalizeRobotCleanerRows(result: unknown, selectedMonth: string): AccountRow[] {
  if (!Array.isArray(result)) return [];

  return result
    .filter((item): item is AccountRow => Boolean(item && typeof item === "object"))
    .filter((item) => item.reservDate?.startsWith(selectedMonth))
    .map((item, index) => ({
      ...item,
      pk: `${selectedMonth}/${ROBOT_CLEANER_LABEL}`,
      facilityLabel: ROBOT_CLEANER_LABEL,
      sk: item.sk || `${ROBOT_CLEANER}#${item.reservDate || selectedMonth}#${index}`,
      reservDt: item.reservDt || item.reservDate || "이용일 정보 없음",
    }));
}

const ReservationAccount = ({ userState }: PropsType) => {
  const [isLoading, setIsLoading] = useState(false);
  const [accountList, setAccountList] = useState<AccountRow[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectMonth, setSelectMonth] = useState("");
  const [months, setMonths] = useState<Month[]>([]);
  const hasLoadedInitialMonth = useRef(false);

  useEffect(() => {
    const currentDate = new Date();
    const generatedMonths = Array.from({ length: 7 }, (_, index) => {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1 - index, 1);
      const yearMonth = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}`;
      return {
        value: yearMonth,
        label: `${date.getFullYear()}년 ${String(date.getMonth() + 1).padStart(2, "0")}월`,
      };
    });

    setMonths(generatedMonths);
    setSelectMonth(generatedMonths[1]?.value || "");
  }, []);

  const selectedMonthLabel = months.find((month) => month.value === selectMonth)?.label || selectMonth;

  const activeReservations = useMemo(
    () => accountList.filter((row) => row.reservState === ACTIVE_RESERVATION_STATE),
    [accountList],
  );

  const billableReservations = useMemo(() => {
    const billableRows = activeReservations.filter((row) => getCommunityPrice(row) > 0);
    const otherRows = billableRows.filter((row) => !["BOOKCAFE", "TABLETENNIS"].includes(getFacilityCode(row)));
    const hourlyGroups = new Map<string, AccountRow[]>();

    billableRows
      .filter((row) => ["BOOKCAFE", "TABLETENNIS"].includes(getFacilityCode(row)))
      .forEach((row) => {
        const key = `${getFacilityCode(row)}#${row.reservationId || row.sk}#${row.reservDt || row.reservDate || ""}`;
        hourlyGroups.set(key, [...(hourlyGroups.get(key) || []), row]);
      });

    const groupedHourlyRows = Array.from(hourlyGroups.entries()).map(([groupKey, rows]) => {
      const slots = rows.map((row) => row.slot || row.useTime || "").filter(Boolean).sort();
      return {
        ...rows[0],
        sk: groupKey,
        facilityLabel: getFacilityLabel(rows[0]),
        communityPrice: rows.reduce((sum, row) => sum + getCommunityPrice(row), 0),
        useTime: slots.join(","),
      };
    });

    return [...otherRows, ...groupedHourlyRows].sort((a, b) =>
      getReservationDate(a).localeCompare(getReservationDate(b), "ko-KR"),
    );
  }, [activeReservations]);

  const screenHistory = useMemo<ScreenHistoryEntry[]>(
    () =>
      activeReservations
        .filter((row) => getFacilityCode(row) === "SCREEN")
        .flatMap((row) => {
          const screenDate = formatScreenDate(row, selectMonth);
          if (!screenDate) return [];

          const device = getReservationDevice(row);
          if (!device) return [];

          return [
            {
              row,
              ...screenDate,
              deviceLabel: device === "ROOM" ? "스크린룸" : device,
              timeLabel: getReservationTime(row) || "시간 정보 없음",
            },
          ];
        })
        .sort((a, b) => b.dateKey.localeCompare(a.dateKey)),
    [activeReservations, selectMonth],
  );

  const totalPrice = useMemo(
    () => billableReservations.reduce((total, row) => total + getCommunityPrice(row), 0),
    [billableReservations],
  );

  const resetResults = () => {
    setAccountList([]);
    setHasSearched(false);
    setErrorMessage("");
  };

  const getAccountList = useCallback(async (selectedMonth: string) => {
    if (!selectedMonth) return;

    setIsLoading(true);
    resetResults();

    try {
      const [accountResponse, robotCleanerResponse] = await Promise.all([
        mainApi.get_my_account_all_list({
          pk: userState.userId,
          sk: selectedMonth,
          selectState: "ALL",
        }),
        mainApi.get_my_robot_cleaner_reservation({
          userId: userState.userId,
          fromDate: `${selectedMonth}01`,
        }),
      ]);

      if (accountResponse.code !== "200" && accountResponse.code !== "400") {
        throw new Error(accountResponse.response?.error_msg || "커뮤니티 청구 데이터 조회 실패");
      }
      if (robotCleanerResponse.code !== "200" && robotCleanerResponse.code !== "400") {
        throw new Error(robotCleanerResponse.response?.error_msg || "로봇청소기 청구 데이터 조회 실패");
      }

      const communityRows =
        accountResponse.code === "200" && Array.isArray(accountResponse.response?.result)
          ? (accountResponse.response.result as AccountRow[])
          : [];
      const robotCleanerRows =
        robotCleanerResponse.code === "200"
          ? normalizeRobotCleanerRows(robotCleanerResponse.response?.result, selectedMonth)
          : [];

      setAccountList([...communityRows, ...robotCleanerRows]);
      setHasSearched(true);
    } catch (error) {
      console.error("ReservationAccount load failed", error);
      setErrorMessage("청구서를 불러오지 못했습니다. 잠시 후 다시 조회해 주세요.");
    } finally {
      setIsLoading(false);
    }
  }, [userState.userId]);

  useEffect(() => {
    if (!selectMonth || hasLoadedInitialMonth.current) return;

    hasLoadedInitialMonth.current = true;
    void getAccountList(selectMonth);
  }, [getAccountList, selectMonth]);

  const handleMonthChange = (event: SelectChangeEvent) => {
    setSelectMonth(event.target.value);
    resetResults();
  };

  return (
    <main className="hpoint-renewal hp-reservation-account-page">
      <section className="hp-reservation-account-search" aria-label="월별 청구서 조회">
        <FormControl fullWidth className="hp-reservation-account-search__month">
          <InputLabel id="reservation-account-month-label">기준월</InputLabel>
          <Select
            labelId="reservation-account-month-label"
            value={selectMonth}
            label="기준월"
            onChange={handleMonthChange}
          >
            {months.map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          aria-label="청구서 조회"
          className="hp-button hp-button--primary hp-reservation-account-search__button"
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => void getAccountList(selectMonth)}
        >
          조회
        </Button>
      </section>

      {errorMessage ? (
        <section className="hp-reservation-account-error" role="alert">
          <InfoOutlinedIcon aria-hidden="true" />
          <Typography>{errorMessage}</Typography>
        </section>
      ) : null}

      {!hasSearched && !errorMessage ? (
        <section className="hp-reservation-account-guide" aria-label="청구서 조회 안내">
          <EventNoteRoundedIcon aria-hidden="true" />
          <Typography>기준월을 선택한 뒤 조회해 주세요.</Typography>
        </section>
      ) : null}

      {hasSearched ? (
        <>
          <section className="hp-reservation-account-summary" aria-label={`${selectedMonthLabel} 청구 요약`}>
            <Box className="hp-reservation-account-summary__icon" aria-hidden="true">
              <EventNoteRoundedIcon />
            </Box>
            <Box className="hp-reservation-account-summary__content">
              <span>{selectedMonthLabel}</span>
              <Typography component="h2">총 청구 금액</Typography>
              <strong>{formatCurrency(totalPrice)}</strong>
              <small>유료 청구 {billableReservations.length}건 기준</small>
            </Box>
          </section>

          {billableReservations.length > 0 ? (
            <section className="hp-reservation-account-section" aria-labelledby="account-charge-title">
              <Box className="hp-reservation-account-section__heading">
                <Typography component="h2" id="account-charge-title">
                  청구 상세
                </Typography>
                <span>{billableReservations.length}건</span>
              </Box>
              <Box className="hp-reservation-account-receipt-sheet">
                {billableReservations.map((row, index) => {
                  const facilityLabel = getFacilityLabel(row);
                  const reservationDate = getReservationDate(row);
                  const reservationTime = getReservationTime(row);
                  const showsReservationTime =
                    getReservationDevice(row) === "ROOM" || ["BOOKCAFE", "TABLETENNIS"].includes(getFacilityCode(row));

                  return (
                    <Box
                      component="article"
                      aria-label={`${facilityLabel} ${reservationDate} 청구 내역`}
                      className="hp-reservation-account-receipt"
                      key={row.sk || `${row.pk}-${index}`}
                    >
                      <Box className="hp-reservation-account-receipt__main">
                        <Box className="hp-reservation-account-receipt__content">
                          <Typography component="h3">{facilityLabel}</Typography>
                          <Typography component="p">{reservationDate}</Typography>
                          {row.inputDt ? <small>{row.inputDt} 신청</small> : null}
                        </Box>
                        <strong>{formatCurrency(getCommunityPrice(row))}</strong>
                      </Box>
                      {showsReservationTime && reservationTime ? (
                        <Box className="hp-reservation-account-receipt__time">
                          <span>이용시간</span>
                          <strong>{reservationTime}</strong>
                        </Box>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            </section>
          ) : null}

          {screenHistory.length > 0 ? (
            <section className="hp-reservation-account-section" aria-labelledby="screen-history-title">
              <Box className="hp-reservation-account-section__heading">
                <Box className="hp-reservation-account-section__title-with-icon">
                  <SportsGolfRoundedIcon aria-hidden="true" />
                  <Typography component="h2" id="screen-history-title">
                    스크린골프 예약 기록
                  </Typography>
                </Box>
                <span>{screenHistory.length}건</span>
              </Box>
              <Box className="hp-reservation-account-history-list">
                {screenHistory.map(({ row, dateKey, dateLabel, deviceLabel, timeLabel }, index) => {
                  const price = getCommunityPrice(row);
                  return (
                    <Box
                      component="article"
                      aria-label={`${deviceLabel} ${dateLabel} 스크린골프 예약 기록`}
                      className="hp-reservation-account-history"
                      key={row.sk || `${dateKey}-${deviceLabel}-${index}`}
                    >
                      <Box className="hp-reservation-account-history__heading">
                        <Box>
                          <span>{deviceLabel}</span>
                          <Typography component="h3">{dateLabel}</Typography>
                        </Box>
                        <strong className={price > 0 ? "is-billable" : "is-excluded"}>
                          {price > 0 ? `청구 포함 · ${formatCurrency(price)}` : "청구 제외"}
                        </strong>
                      </Box>
                      <Box className="hp-reservation-account-history__time">
                        <span>예약시간</span>
                        <strong>{timeLabel}</strong>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </section>
          ) : null}

          {billableReservations.length === 0 && screenHistory.length === 0 ? (
            <section className="hp-reservation-account-empty" aria-label="청구 내역 없음">
              <InfoOutlinedIcon aria-hidden="true" />
              <Typography component="h2">선택한 월에 청구 또는 스크린골프 예약 기록이 없습니다.</Typography>
              <Typography>다른 기준월을 선택해 다시 조회해 주세요.</Typography>
            </section>
          ) : null}

          <aside className="hp-reservation-account-notice">
            <InfoOutlinedIcon aria-hidden="true" />
            <Typography>
              청구 금액은 선택월의 신청 상태와 저장된 금액 기준입니다. 스크린골프 타석 예약은 청구 금액에
              포함되지 않습니다.
            </Typography>
          </aside>
        </>
      ) : null}

      <LoadingCircle loading={isLoading} />
    </main>
  );
};

export default ReservationAccount;
